import { z } from "zod";
import { OffsetPageQuerySchema } from "@/shared/contracts/base";
import { ShopCurrencySchema } from "@/shared/lib/currency";

/** DTO / Schema ฝั่ง shop roles — ใช้กับ API และ validation */
export const CreateShopRoleSchema = z.object({
  name: z.string().min(1).max(100),
  permissions: z.array(z.string()),
  sortOrder: z.number().int().optional(),
});
export type CreateShopRoleDTO = z.infer<typeof CreateShopRoleSchema>;

export const UpdateShopRoleSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  permissions: z.array(z.string()).optional(),
  sortOrder: z.number().int().optional(),
});
export type UpdateShopRoleDTO = z.infer<typeof UpdateShopRoleSchema>;

export const RoleIdParamSchema = z.object({ roleId: z.string().min(1) });
export type RoleIdParamDTO = z.infer<typeof RoleIdParamSchema>;

/** DTO / Schema ฝั่ง shop staff (พนักงานในร้าน) */
export const CreateShopStaffSchema = z.object({
  userId: z.string().min(1),
  shopRoleId: z.string().min(1).optional(),
  branchId: z.string().uuid().optional().nullable(),
});
export type CreateShopStaffDTO = z.infer<typeof CreateShopStaffSchema>;

export const UpdateShopStaffSchema = z.object({
  shopRoleId: z.string().min(1).optional().nullable(),
  branchId: z.string().uuid().optional().nullable(),
  isActive: z.boolean().optional(),
});
export type UpdateShopStaffDTO = z.infer<typeof UpdateShopStaffSchema>;

export const StaffIdParamSchema = z.object({ staffId: z.string().uuid() });
export type StaffIdParamDTO = z.infer<typeof StaffIdParamSchema>;

/** สร้างบัญชี user + ผูกเป็น staff ในร้าน */
export const CreateStaffWithAccountSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1),
  password: z.string().min(6).optional(),
  shopRoleId: z.string().uuid().optional().nullable(),
  branchId: z.string().uuid().optional().nullable(),
});
export type CreateStaffWithAccountDTO = z.infer<
  typeof CreateStaffWithAccountSchema
>;

/** แก้ไขทั้ง user และ staff */
export const UpdateStaffWithAccountSchema = z.object({
  email: z.string().email().optional(),
  name: z.string().min(1).optional(),
  password: z.string().min(6).optional(),
  shopRoleId: z.string().uuid().optional().nullable(),
  branchId: z.string().uuid().optional().nullable(),
  isActive: z.boolean().optional(),
});
export type UpdateStaffWithAccountDTO = z.infer<
  typeof UpdateStaffWithAccountSchema
>;

/** Query รายการสินค้าขายได้ของร้าน (POS catalog) */
export const CatalogProductTypeSchema = z.enum([
  "part_new",
  "part_oem",
  "part_aftermarket",
  "part_used",
  "service",
]);
export type CatalogProductTypeDTO = z.infer<typeof CatalogProductTypeSchema>;

export const SellableCatalogQuerySchema = OffsetPageQuerySchema.extend({
  q: z
    .string()
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
  categoryId: z.string().uuid().optional(),
  /** ກັ່ນຕາມ catalog_products.type */
  productType: CatalogProductTypeSchema.optional(),
  /** ກັ່ນຕາມ catalog_skus.fitment_status (catalog UX: ລໍ fitment / ພ້ອມ marketplace) */
  fitment: z.enum(["pending", "complete"]).optional(),
  /** ກັ່ນຕາມ shop_product_skus.currency (PO / combobox ທີ່ຕ້ອງກົງຫົວເອກະສານ) */
  currency: ShopCurrencySchema.optional(),
  /** ຄົ້ນແບບຕົງກັນຈາກ shop_product_skus.barcode ເທົ່ານັ້ນ (POS scan) */
  barcode: z
    .string()
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
  /** POS browse: ສະຕ໋ອກຕາມສາຂາ (catalog ບໍ່ສົ່ງ) */
  branchId: z.string().uuid().optional(),
  /** Catalog management list ONLY — also show this shop's own pending candidates.
   *  Other flows (POS sell/scan, reserve, PO, adjust…) omit it → active-only. */
  includePending: z.coerce.boolean().optional(),
  /** Catalog management list ONLY — also show this shop's own deactivated SKUs. */
  includeInactive: z.coerce.boolean().optional(),
});
export type SellableCatalogQueryDTO = z.infer<
  typeof SellableCatalogQuerySchema
>;

/** POS — ໝວດທີ່ມີສິນຄ້າຂາຍໄດ້ + top ຕາມຍອດຂາຍ */
export const PosCategoriesQuerySchema = z.object({
  branchId: z.string().uuid().optional(),
  /** ຊ່ວງວັນສຳລັບ top categories (default 30) */
  topDays: z.coerce.number().int().min(1).max(90).optional().default(30),
});
export type PosCategoriesQueryDTO = z.infer<typeof PosCategoriesQuerySchema>;

/** Lookup หมวดหมู่สินค้าขายได้ (InfiniteCombobox / POS catalog filter) */
export const SellableCategoryLookupQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  search: z
    .string()
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
});
export type SellableCategoryLookupQueryDTO = z.infer<
  typeof SellableCategoryLookupQuerySchema
>;

export const ShopProductSkuIdParamSchema = z.object({
  shopProductSkuId: z.string().uuid(),
});
export type ShopProductSkuIdParamDTO = z.infer<
  typeof ShopProductSkuIdParamSchema
>;

/** เพิ่มรูป SKU — ส่ง url (storage key จาก presign upload) */
export const ShopSkuMediaAddSchema = z.object({
  url: z.string().min(1, "ຕ້ອງສົ່ງ url (key) ຂອງຮູບ"),
});
export type ShopSkuMediaAddDTO = z.infer<typeof ShopSkuMediaAddSchema>;

/** จัดลำดับรูป SKU — index = sort_order (รูปแรก = thumbnail) */
export const ShopSkuMediaReorderSchema = z.object({
  mediaIds: z
    .array(z.string().uuid())
    .min(1, "ຕ້ອງສົ່ງລາຍການ media_id ຢ່າງໜ້ອຍ 1 ອັນ"),
});
export type ShopSkuMediaReorderDTO = z.infer<typeof ShopSkuMediaReorderSchema>;

export const ShopSkuMediaIdParamSchema = z.object({
  shopProductSkuId: z.string().uuid(),
  mediaId: z.string().uuid(),
});
export type ShopSkuMediaIdParamDTO = z.infer<typeof ShopSkuMediaIdParamSchema>;

/** PATCH ราคา / SKU / barcode / cost / warranty defaults ต่อร้าน (shop_product_skus) */
export const UpdateShopProductSkuSchema = z
  .object({
    sku: z.string().min(1).max(100).optional(),
    price: z.number().finite().nonnegative().optional(),
    currency: ShopCurrencySchema.optional(),
    barcode: z
      .union([z.string().max(100), z.null()])
      .optional()
      .transform((v) => (v === "" ? null : v)),
    cost: z.union([z.number().finite().nonnegative(), z.null()]).optional(),
    trackingMode: z.enum(["none", "lot", "lot_serial"]).optional(),
    /** Update category on linked catalog_products */
    categoryId: z.string().uuid().optional(),
    /** 0 = ບໍ່ມີປະກັນ; null = ລ້າງຄ່າເລີ່ມຕົ້ນ */
    warrantyDurationMonths: z
      .union([z.number().int().min(0).max(1200), z.null()])
      .optional(),
    warrantyMaxOdometerKm: z
      .union([z.number().int().min(0).max(2147483647), z.null()])
      .optional(),
    warrantyTermsNote: z
      .union([z.string().max(5000), z.null()])
      .optional()
      .transform((v) => (v === "" ? null : v)),
  })
  .refine(
    (d) =>
      d.sku !== undefined ||
      d.price !== undefined ||
      d.currency !== undefined ||
      d.barcode !== undefined ||
      d.cost !== undefined ||
      d.trackingMode !== undefined ||
      d.categoryId !== undefined ||
      d.warrantyDurationMonths !== undefined ||
      d.warrantyMaxOdometerKm !== undefined ||
      d.warrantyTermsNote !== undefined,
    { message: "ຕ້ອງມີຢ່າງນ້ອຍໜຶ່ງຟິລດ໌ທີ່ຈະອັບເດດ" },
  );
export type UpdateShopProductSkuDTO = z.infer<
  typeof UpdateShopProductSkuSchema
>;

/** ເປີດ/ປິດ SKU ຂາຍໄດ້ */
export const SetSkuActiveSchema = z.object({
  isActive: z.boolean(),
});
export type SetSkuActiveDTO = z.infer<typeof SetSkuActiveSchema>;

/** ບໍລິການ (service) — CRUD */
export const ServiceListQuerySchema = OffsetPageQuerySchema.extend({
  q: z
    .string()
    .optional()
    .transform((v) => (v?.trim() ? v.trim() : undefined)),
});
export type ServiceListQueryDTO = z.infer<typeof ServiceListQuerySchema>;

export const CreateServiceSchema = z.object({
  name: z.string().min(1).max(255),
  price: z.number().finite().nonnegative(),
  currency: ShopCurrencySchema.optional(),
});
export type CreateServiceDTO = z.infer<typeof CreateServiceSchema>;

export const UpdateServiceSchema = z
  .object({
    name: z.string().min(1).max(255).optional(),
    price: z.number().finite().nonnegative().optional(),
    currency: ShopCurrencySchema.optional(),
    isActive: z.boolean().optional(),
  })
  .refine(
    (d) =>
      d.name !== undefined ||
      d.price !== undefined ||
      d.currency !== undefined ||
      d.isActive !== undefined,
    { message: "ຕ້ອງມີຢ່າງນ້ອຍໜຶ່ງຟິລດ໌ທີ່ຈະອັບເດດ" },
  );
export type UpdateServiceDTO = z.infer<typeof UpdateServiceSchema>;

/** Query ຄົ້ນ catalog SKU ກ່ອນ provision (dedup) */
export const CatalogMatchQuerySchema = z.object({
  q: z.string().max(200).optional(),
  oemPartNo: z.string().max(100).optional(),
  barcode: z.string().max(100).optional(),
  limit: z.coerce.number().int().min(1).max(50).optional(),
});
export type CatalogMatchQueryDTO = z.infer<typeof CatalogMatchQuerySchema>;

/** Fuzzy suggest master (Phase 2 — suggest only, no auto-link) */
export const CatalogSuggestQuerySchema = z.object({
  name: z.string().min(2).max(255),
  brandId: z.string().uuid().optional(),
  categoryId: z.string().uuid().optional(),
  type: z
    .enum(["part_new", "part_oem", "part_aftermarket", "part_used", "service"])
    .optional(),
  limit: z.coerce.number().int().min(1).max(20).optional(),
});
export type CatalogSuggestQueryDTO = z.infer<typeof CatalogSuggestQuerySchema>;

/** Browse master catalog ตามหมวด — เลือกผูก offer (POS) */
export const LinkableCatalogQuerySchema = z.object({
  categoryId: z.string().uuid(),
  q: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(80).optional(),
});
export type LinkableCatalogQueryDTO = z.infer<
  typeof LinkableCatalogQuerySchema
>;

/** POS ສ້າງໃຫ້ຂາຍໄດ້ທັນທີ + ຄິວ Admin ຄີ fitment */
export const ProvisionPosSellableProductSchema = z
  .object({
    name: z
      .string()
      .max(255)
      .optional()
      .transform((v) => {
        if (v === undefined || v === "") return undefined;
        const t = v.trim();
        return t === "" ? undefined : t;
      }),
    description: z
      .string()
      .max(5000)
      .optional()
      .transform((v) => {
        if (v === undefined || v === "") return undefined;
        const t = v.trim();
        return t === "" ? undefined : t;
      }),
    type: z
      .enum([
        "part_new",
        "part_oem",
        "part_aftermarket",
        "part_used",
        "service",
      ])
      .default("part_new"),
    shopSku: z.string().min(1).max(100),
    barcode: z
      .string()
      .max(100)
      .optional()
      .transform((v) => {
        if (v === undefined || v === "") return undefined;
        const t = v.trim();
        return t === "" ? undefined : t;
      }),
    price: z.coerce.number().min(0),
    currency: z.enum(["LAK", "THB", "USD"]).default("LAK"),
    globalSku: z
      .string()
      .max(100)
      .optional()
      .transform((v) => {
        if (v === undefined || v === "") return undefined;
        const t = v.trim();
        return t === "" ? undefined : t;
      }),
    categoryId: z.string().uuid("ຕ້ອງເລືອກໝວດ").optional(),
    catalogSkuId: z.string().uuid().optional(),
  })
  .superRefine((d, ctx) => {
    if (d.catalogSkuId) return;
    if (!d.name) {
      ctx.addIssue({
        code: "custom",
        path: ["name"],
        message: "ຕ້ອງມີຊື່ສິນຄ້າ",
      });
    }
    if (!d.categoryId) {
      ctx.addIssue({
        code: "custom",
        path: ["categoryId"],
        message: "ຕ້ອງເລືອກໝວດ",
      });
    }
  });
export type ProvisionPosSellableProductDTO = z.infer<
  typeof ProvisionPosSellableProductSchema
>;

/** Query ລາຍການສະຕ໋ອກຕາມສິນຄ້າຂາຍໄດ້ (Phase E) */
export const ShopInventoryListQuerySchema = OffsetPageQuerySchema.extend({
  q: z
    .string()
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
  categoryId: z.string().uuid().optional(),
  branchId: z.string().uuid().optional(),
  /** ກອງແຖວດຽວຕາມ SKU (ຮ່ວມກັບ branchId ສຳລັບຍອດຕາມສາຂາ) */
  shopProductSkuId: z.string().uuid().optional(),
  /** Phase W — ກອງ SKU ທີ່ມີ qty ໃນຊ່ອງ (ຕ້ອງມີ branchId) */
  locationId: z.string().uuid().optional(),
});
export type ShopInventoryListQueryDTO = z.infer<
  typeof ShopInventoryListQuerySchema
>;

/** Query lot labels (tracking_mode=lot) ສຳລັບສະແດງ/ແກ້ lot_scan_code */
export const ShopInventoryLotLabelsQuerySchema = z.object({
  branchId: z.string().uuid(),
  shopProductSkuId: z.string().uuid(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ShopInventoryLotLabelsQueryDTO = z.infer<
  typeof ShopInventoryLotLabelsQuerySchema
>;

/** Query serial in_stock ຕໍ່ສາຂາ+SKU — ສຳລັບໜ້າລາຍລະອຽດສາງ */
export const ShopInventorySerialsQuerySchema = z.object({
  branchId: z.string().uuid(),
  shopProductSkuId: z.string().uuid(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
  /** ກອງ serial ຕາມ lot (ຕ້ອງເປັນ lot ຂອງ SKU+ສາຂານີ້) */
  lotId: z.string().uuid().optional(),
});
export type ShopInventorySerialsQueryDTO = z.infer<
  typeof ShopInventorySerialsQuerySchema
>;

/** Lookup lot ທີ່ມີ serial in_stock (InfiniteCombobox ກອງ serial) */
export const ShopInventoryLotsWithSerialsLookupQuerySchema = z.object({
  branchId: z.string().uuid(),
  shopProductSkuId: z.string().uuid(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  search: z
    .string()
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
});
export type ShopInventoryLotsWithSerialsLookupQueryDTO = z.infer<
  typeof ShopInventoryLotsWithSerialsLookupQuerySchema
>;

export const ShopInventoryLotSerialFilterDetailQuerySchema = z.object({
  branchId: z.string().uuid(),
  shopProductSkuId: z.string().uuid(),
});
export type ShopInventoryLotSerialFilterDetailQueryDTO = z.infer<
  typeof ShopInventoryLotSerialFilterDetailQuerySchema
>;

export const ShopInventoryLotIdParamSchema = z.object({
  lotId: z.string().uuid(),
});
export type ShopInventoryLotIdParamDTO = z.infer<
  typeof ShopInventoryLotIdParamSchema
>;

export const ShopInventoryLotScanCodePatchSchema = z
  .object({
    lotScanCode: z
      .string()
      .max(100)
      .optional()
      .nullable()
      .transform((v) => {
        if (v == null) return null;
        const t = String(v).trim();
        return t === "" ? null : t;
      }),
    regenerate: z.boolean().optional(),
  })
  .superRefine((b, ctx) => {
    if (b.regenerate !== true && b.lotScanCode === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ຕ້ອງສົ່ງ lotScanCode ຫຼື regenerate=true",
      });
    }
  });
export type ShopInventoryLotScanCodePatchDTO = z.infer<
  typeof ShopInventoryLotScanCodePatchSchema
>;

/** ປະເພດ movement ຕາມ inventory_movements.type */
export const InventoryMovementTypeSchema = z.enum([
  "receive",
  "sale",
  "refund",
  "adjust",
  "transfer_out",
  "transfer_in",
  "reserve",
  "release",
]);
export type InventoryMovementTypeDTO = z.infer<
  typeof InventoryMovementTypeSchema
>;

/** Query ລາຍການ inventory_movements (L4 ledger) */
export const ShopInventoryMovementsQuerySchema = OffsetPageQuerySchema.extend({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  branchId: z.string().uuid().optional(),
  shopProductSkuId: z.string().uuid().optional(),
  type: InventoryMovementTypeSchema.optional(),
  createdFrom: z
    .string()
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
  createdTo: z
    .string()
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
});
export type ShopInventoryMovementsQueryDTO = z.infer<
  typeof ShopInventoryMovementsQuerySchema
>;

/** Query ສະຫຼຸບ reconcile lot/serial vs on_hand (L4) */
export const ShopInventoryReconcileQuerySchema = z.object({
  branchId: z.string().uuid().optional(),
  /** ກອງແຖວດຽວຕາມ SKU (ຮ່ວມ branchId) */
  shopProductSkuId: z.string().uuid().optional(),
  /** query string: ຂາດ = true; "false" / "0" = ສະແດງທັງໝົດ */
  onlyMismatch: z
    .string()
    .optional()
    .transform((s) => {
      if (s === undefined || s === "") return true;
      const t = s.trim().toLowerCase();
      return t !== "false" && t !== "0";
    }),
  limit: z.coerce.number().int().min(1).max(500).default(100),
});
export type ShopInventoryReconcileQueryDTO = z.infer<
  typeof ShopInventoryReconcileQuerySchema
>;

/** Lookup ສາຂາໃນຮ້ານ (InfiniteCombobox — ເຊັ່ນ filter inventory) */
export const ShopBranchLookupQuerySchema = SellableCategoryLookupQuerySchema;
export type ShopBranchLookupQueryDTO = z.infer<
  typeof ShopBranchLookupQuerySchema
>;

export const ShopBranchIdParamSchema = z.object({
  branchId: z.string().uuid(),
});
export type ShopBranchIdParamDTO = z.infer<typeof ShopBranchIdParamSchema>;

export const CreateShopBranchSchema = z.object({
  name: z.string().min(1).max(255),
  phone: z
    .string()
    .max(50)
    .optional()
    .nullable()
    .transform((v) => (v === undefined || v === "" ? null : v)),
  address: z
    .string()
    .max(4000)
    .optional()
    .nullable()
    .transform((v) => (v === undefined || v === "" ? null : v)),
  /** ທັບສະກຸນຮ້ານ; ບໍ່ສົ່ງ = ສືບທອດຈາກຮ້ານ */
  defaultCurrency: ShopCurrencySchema.optional().nullable(),
});
export type CreateShopBranchDTO = z.infer<typeof CreateShopBranchSchema>;

export const UpdateShopBranchSchema = z.object({
  name: z.string().min(1).max(255).optional(),
  phone: z
    .string()
    .max(50)
    .optional()
    .nullable()
    .transform((v) => (v === undefined ? undefined : v === "" ? null : v)),
  address: z
    .string()
    .max(4000)
    .optional()
    .nullable()
    .transform((v) => (v === undefined ? undefined : v === "" ? null : v)),
  isActive: z.boolean().optional(),
  /** null = ສືບທອດຈາກຮ້ານ; ບໍ່ສົ່ງ = ບໍ່ປ່ຽນ */
  defaultCurrency: ShopCurrencySchema.nullable().optional(),
});
export type UpdateShopBranchDTO = z.infer<typeof UpdateShopBranchSchema>;

/** ຕັ້ງຄ່າຮ້ານ (POS ສະກຸນເລີ່ມຕົ້ນ + VAT + MC2) */
export const UpdateShopSettingsSchema = z
  .object({
    defaultCurrency: ShopCurrencySchema.optional(),
    defaultTaxPercent: z.number().finite().min(0).max(100).optional(),
    enforceSingleShopSkuCurrency: z.boolean().optional(),
  })
  .refine(
    (d) =>
      d.defaultCurrency !== undefined ||
      d.defaultTaxPercent !== undefined ||
      d.enforceSingleShopSkuCurrency !== undefined,
    { message: "ຕ້ອງມີຢ່າງນ້ອຍໜຶ່ງຟິລດ໌ທີ່ຈະອັບເດດ" },
  );
export type UpdateShopSettingsDTO = z.infer<typeof UpdateShopSettingsSchema>;

/** MC3: ອັດຕາແລກປ່ຽນ (ລາຍງານ / ຊຳລະ — ບໍ່ແຕະ ledger POS) */
export const ExchangeRateQuerySchema = z.object({
  asOf: z
    .string()
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? undefined : d;
    }),
});
export type ExchangeRateQueryDTO = z.infer<typeof ExchangeRateQuerySchema>;

/** ລາຍການປະຫວັດອັດຕາ (ແຖວຮ້ານເທົ່ານັ້ນ — ບໍ່ລວມ global) */
export const ExchangeRateHistoryQuerySchema = z.object({
  baseCurrency: ShopCurrencySchema.optional(),
  quoteCurrency: ShopCurrencySchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(30),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ExchangeRateHistoryQueryDTO = z.infer<
  typeof ExchangeRateHistoryQuerySchema
>;

export const CreateExchangeRateSchema = z
  .object({
    baseCurrency: ShopCurrencySchema,
    quoteCurrency: ShopCurrencySchema,
    rate: z.number().finite().positive(),
    effectiveFrom: z.coerce.date().optional(),
    source: z.string().max(100).optional(),
  })
  .refine((d) => d.baseCurrency !== d.quoteCurrency, {
    message: "base ແລະ quote ຕ້ອງຄົນລະສະກຸນ",
  });
export type CreateExchangeRateDTO = z.infer<typeof CreateExchangeRateSchema>;

const BulkExchangeRateLineSchema = z
  .object({
    baseCurrency: ShopCurrencySchema,
    quoteCurrency: ShopCurrencySchema,
    rate: z.number().finite().positive(),
  })
  .refine((d) => d.baseCurrency !== d.quoteCurrency, {
    message: "base ແລະ quote ຕ້ອງຄົນລະສະກຸນ",
  });

export const BulkCreateExchangeRatesSchema = z.object({
  rates: z.array(BulkExchangeRateLineSchema).min(1).max(32),
  effectiveFrom: z.coerce.date().optional(),
  source: z.string().max(100).optional(),
});
export type BulkCreateExchangeRatesDTO = z.infer<
  typeof BulkCreateExchangeRatesSchema
>;

export const ConvertCurrencySchema = z.object({
  amount: z.number().finite().nonnegative(),
  fromCurrency: ShopCurrencySchema,
  toCurrency: ShopCurrencySchema,
  asOf: z
    .string()
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? undefined : d;
    }),
});
export type ConvertCurrencyDTO = z.infer<typeof ConvertCurrencySchema>;

/** ປັບສະຕ໋ອກດ້ວຍມື (ນับ, ຂອງເສຍ, ແກ້ຄ່າ) */
export const ShopInventoryAdjustSchema = z.object({
  shopProductSkuId: z.string().uuid(),
  branchId: z.string().uuid(),
  delta: z.number().int(),
  reason: z.string().min(1).max(2000),
  adjustmentType: z.enum(["count", "damage", "correction"]),
});
export type ShopInventoryAdjustDTO = z.infer<typeof ShopInventoryAdjustSchema>;

/** ບັນທຶກຮັບເຂົ້າ (L0) — ໂຄງສ້າງຕາມ tracking_mode ຂອງ SKU */
export const ShopInventoryReceiptLotLineSchema = z.object({
  lotNo: z.string().min(1).max(100),
  qty: z.number().int().positive().max(999_999),
  /** ວັນທີຜະລິດ (YYYY-MM-DD ຫຼື ISO) */
  manufacturedDate: z.string().max(32).optional().nullable(),
  expiryDate: z.string().max(32).optional().nullable(),
});

/** ຮັບ serial ພ້ອມ lot + (ທາງເລືອກ) ລະຫັດສະແກນຫນ່ວຍ — ຕໍ່ຊິ້ນ */
export const ShopInventorySerialReceiptEntrySchema = z.object({
  serialNo: z.string().min(1).max(100),
  lotNo: z.string().min(1).max(100),
  unitScanCode: z.string().min(1).max(100).optional().nullable(),
});

export const ShopInventoryReceiptLineSchema = z.object({
  shopProductSkuId: z.string().uuid(),
  /** Phase W — ຊ່ອງຮັບ (override default) */
  locationId: z.string().uuid().optional(),
  /** tracking_mode = none */
  qty: z.number().int().positive().max(999_999).optional(),
  /** tracking_mode = lot */
  lots: z.array(ShopInventoryReceiptLotLineSchema).max(50).optional(),
  /** tracking_mode = lot_serial — serial + lot + (ທາງເລືອກ) unit scan ຕໍ່ຊິ້ນ */
  serialReceipts: z
    .array(ShopInventorySerialReceiptEntrySchema)
    .max(200)
    .optional(),
});

export const ShopInventoryGoodsReceiptSchema = z.object({
  branchId: z.string().uuid(),
  /** Phase W — default location ທຸກບັນທັດ (line.locationId override ได้) */
  defaultLocationId: z.string().uuid().optional(),
  lines: z.array(ShopInventoryReceiptLineSchema).min(1).max(50),
  note: z
    .string()
    .max(2000)
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
});

export type ShopInventoryGoodsReceiptDTO = z.infer<
  typeof ShopInventoryGoodsReceiptSchema
>;

/** P1: ຮັບຂອງຕາມ PO — ບໍ່ກັ່ນ branchId (ໃຊ້ສາຂາຈາກ PO); ໂຄງສ້າງບັນທັດຄືກັນກັບຮັບທົ່ວໄປ */
export const PurchaseOrderGoodsReceiptSchema = z.object({
  /** Phase W — default location ທຸກບັນທັດ (line.locationId override ได้) */
  defaultLocationId: z.string().uuid().optional(),
  lines: z.array(ShopInventoryReceiptLineSchema).min(1).max(50),
  note: z
    .string()
    .max(2000)
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
});
export type PurchaseOrderGoodsReceiptDTO = z.infer<
  typeof PurchaseOrderGoodsReceiptSchema
>;

/** ໂອນສະຕ໋ອກລະຫວ່າງສາຂາ (L3) — ສະເພາະ SKU tracking_mode = none */
export const ShopStockTransferLineSchema = z.object({
  shopProductSkuId: z.string().uuid(),
  qty: z.number().int().positive().max(999_999),
});

export const ShopStockTransferSchema = z.object({
  fromBranchId: z.string().uuid(),
  toBranchId: z.string().uuid(),
  /** Phase W — ຊ່ອງປາຍທາງທີ່ສາຂາຮັບ (optional → default receive) */
  toLocationId: z.string().uuid().optional(),
  lines: z.array(ShopStockTransferLineSchema).min(1).max(100),
  note: z
    .string()
    .max(2000)
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
});
export type ShopStockTransferDTO = z.infer<typeof ShopStockTransferSchema>;

/** ຈອງຍອດພ້ອມຂາຍ (reserved += qty) — ກວດ on_hand - reserved */
export const ShopInventoryReserveSchema = z.object({
  branchId: z.string().uuid(),
  shopProductSkuId: z.string().uuid(),
  qty: z.number().int().positive().max(999_999),
  note: z
    .string()
    .max(2000)
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
});
export type ShopInventoryReserveDTO = z.infer<
  typeof ShopInventoryReserveSchema
>;

/** ປົດຈອງ (reserved -= qty) */
export const ShopInventoryReleaseSchema = ShopInventoryReserveSchema;
export type ShopInventoryReleaseDTO = z.infer<
  typeof ShopInventoryReleaseSchema
>;

// ——— Warehouse locations (Phase W) ———

export const WarehouseZoneListQuerySchema = z.object({
  branchId: z.string().uuid(),
});
export type WarehouseZoneListQueryDTO = z.infer<
  typeof WarehouseZoneListQuerySchema
>;

export const WarehouseZoneCreateSchema = z.object({
  branchId: z.string().uuid(),
  code: z.string().min(1).max(32),
  name: z.string().min(1).max(120),
  sortOrder: z.number().int().min(0).max(9999).optional(),
});
export type WarehouseZoneCreateDTO = z.infer<typeof WarehouseZoneCreateSchema>;

export const WarehouseZoneUpdateSchema = z
  .object({
    code: z.string().min(1).max(32).optional(),
    name: z.string().min(1).max(120).optional(),
    sortOrder: z.number().int().min(0).max(9999).optional(),
  })
  .refine((o) => Object.keys(o).length > 0, {
    message: "At least one field required",
  });
export type WarehouseZoneUpdateDTO = z.infer<typeof WarehouseZoneUpdateSchema>;

export const WarehouseZoneIdParamSchema = z.object({
  zoneId: z.string().uuid(),
});

export const WarehouseLocationListQuerySchema = z.object({
  branchId: z.string().uuid(),
  zoneId: z.string().uuid().optional(),
});
export type WarehouseLocationListQueryDTO = z.infer<
  typeof WarehouseLocationListQuerySchema
>;

export const WarehouseLocationCreateSchema = z.object({
  branchId: z.string().uuid(),
  zoneId: z.string().uuid(),
  code: z.string().min(1).max(64),
  name: z.string().max(120).optional().nullable(),
  aisle: z.string().max(16).optional().nullable(),
  shelf: z.string().max(16).optional().nullable(),
  bin: z.string().max(16).optional().nullable(),
  scanCode: z.string().max(64).optional().nullable(),
  isDefaultReceive: z.boolean().optional(),
});
export type WarehouseLocationCreateDTO = z.infer<
  typeof WarehouseLocationCreateSchema
>;

export const WarehouseLocationUpdateSchema = z
  .object({
    code: z.string().min(1).max(64).optional(),
    name: z.string().max(120).optional().nullable(),
    aisle: z.string().max(16).optional().nullable(),
    shelf: z.string().max(16).optional().nullable(),
    bin: z.string().max(16).optional().nullable(),
    isDefaultReceive: z.boolean().optional(),
  })
  .refine((o) => Object.keys(o).length > 0, {
    message: "At least one field required",
  });
export type WarehouseLocationUpdateDTO = z.infer<
  typeof WarehouseLocationUpdateSchema
>;

export const WarehouseLocationIdParamSchema = z.object({
  locationId: z.string().uuid(),
});

export const WarehouseLocationLookupQuerySchema = z.object({
  scanCode: z.string().min(1).max(64),
});

export const InventoryByLocationQuerySchema = OffsetPageQuerySchema.extend({
  branchId: z.string().uuid(),
  locationId: z.string().uuid(),
  q: z.string().max(200).optional(),
});
export type InventoryByLocationQueryDTO = z.infer<
  typeof InventoryByLocationQuerySchema
>;

export const SkuLocationBalancesQuerySchema = z.object({
  branchId: z.string().uuid(),
});
export type SkuLocationBalancesQueryDTO = z.infer<
  typeof SkuLocationBalancesQuerySchema
>;

export const WarehouseUnlocatedReportQuerySchema = OffsetPageQuerySchema.extend(
  {
    branchId: z.string().uuid(),
    q: z.string().max(200).optional(),
  },
);
export type WarehouseUnlocatedReportQueryDTO = z.infer<
  typeof WarehouseUnlocatedReportQuerySchema
>;

export const WarehouseEmptyLocationsReportQuerySchema =
  OffsetPageQuerySchema.extend({
    branchId: z.string().uuid(),
  });
export type WarehouseEmptyLocationsReportQueryDTO = z.infer<
  typeof WarehouseEmptyLocationsReportQuerySchema
>;

export const WarehouseReceiveByLocationReportQuerySchema =
  OffsetPageQuerySchema.extend({
    branchId: z.string().uuid().optional(),
    locationId: z.string().uuid().optional(),
    receivedFrom: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
    receivedTo: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
  });
export type WarehouseReceiveByLocationReportQueryDTO = z.infer<
  typeof WarehouseReceiveByLocationReportQuerySchema
>;

export const InventoryAssignLocationSchema = z.object({
  branchId: z.string().uuid(),
  shopProductSkuId: z.string().uuid(),
  fromLocationId: z.string().uuid().optional().nullable(),
  toLocationId: z.string().uuid(),
  qty: z.number().int().positive().max(999_999),
  note: z
    .string()
    .max(2000)
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
});
export type InventoryAssignLocationDTO = z.infer<
  typeof InventoryAssignLocationSchema
>;

/** Phase L — warehouse layout preview query (optional floor / layoutId) */
export const WarehouseLayoutQuerySchema = z.object({
  branchId: z.string().uuid(),
  layoutId: z.string().uuid().optional(),
  floorIndex: z.coerce.number().int().min(0).max(100).optional(),
});
export type WarehouseLayoutQueryDTO = z.infer<
  typeof WarehouseLayoutQuerySchema
>;

export const WarehouseLayoutFloorsQuerySchema = z.object({
  branchId: z.string().uuid(),
});
export type WarehouseLayoutFloorsQueryDTO = z.infer<
  typeof WarehouseLayoutFloorsQuerySchema
>;

export const WarehouseLayoutPlacementsQuerySchema = z.object({
  branchId: z.string().uuid(),
});
export type WarehouseLayoutPlacementsQueryDTO = z.infer<
  typeof WarehouseLayoutPlacementsQuerySchema
>;

/** Assign (or clear) a zone's floor binding from the List tab */
export const WarehouseLayoutAssignZoneSchema = z.object({
  branchId: z.string().uuid(),
  zoneId: z.string().uuid(),
  /** Target floor layout; null = remove zone node from all floors */
  layoutId: z.string().uuid().nullable(),
  /** Also move this zone's location nodes onto the floor (default true) */
  moveLocations: z.boolean().optional().default(true),
});
export type WarehouseLayoutAssignZoneDTO = z.infer<
  typeof WarehouseLayoutAssignZoneSchema
>;

export const WarehouseLayoutFloorCreateSchema = z.object({
  branchId: z.string().uuid(),
  name: z.string().min(1).max(120).optional(),
  floorIndex: z.number().int().min(0).max(100).optional(),
});
export type WarehouseLayoutFloorCreateDTO = z.infer<
  typeof WarehouseLayoutFloorCreateSchema
>;

export const WarehouseLayoutNodeInputSchema = z
  .object({
    kind: z.enum(["zone", "location", "obstacle", "label"]),
    refZoneId: z.string().uuid().nullable().optional(),
    refLocationId: z.string().uuid().nullable().optional(),
    x: z.number().int(),
    y: z.number().int(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    rotation: z.number().int().optional().default(0),
    labelOverride: z.string().max(120).nullable().optional(),
    color: z.string().max(16).nullable().optional(),
    zIndex: z.number().int().optional().default(0),
  })
  .superRefine((n, ctx) => {
    const zoneId = n.refZoneId ?? null;
    const locId = n.refLocationId ?? null;
    if (n.kind === "location") {
      if (!locId || zoneId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "location node requires refLocationId only",
        });
      }
    } else if (n.kind === "zone") {
      if (!zoneId || locId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "zone node requires refZoneId only",
        });
      }
    } else if (zoneId || locId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "obstacle/label must not reference zone/location",
      });
    }
  });
export type WarehouseLayoutNodeInputDTO = z.infer<
  typeof WarehouseLayoutNodeInputSchema
>;

export const WarehouseLayoutUpsertSchema = z.object({
  branchId: z.string().uuid(),
  layoutId: z.string().uuid().optional(),
  floorIndex: z.number().int().min(0).max(100).optional(),
  name: z.string().min(1).max(120).optional(),
  canvasWidth: z.number().int().positive().max(50_000).optional(),
  canvasHeight: z.number().int().positive().max(50_000).optional(),
  gridSize: z.number().int().positive().max(500).optional(),
  backgroundImageUrl: z.string().max(2000).nullable().optional(),
  backgroundOpacity: z.number().int().min(0).max(100).optional(),
});
export type WarehouseLayoutUpsertDTO = z.infer<
  typeof WarehouseLayoutUpsertSchema
>;

export const WarehouseLayoutNodesReplaceSchema = z.object({
  branchId: z.string().uuid(),
  layoutId: z.string().uuid().optional(),
  nodes: z.array(WarehouseLayoutNodeInputSchema).max(2000),
});
export type WarehouseLayoutNodesReplaceDTO = z.infer<
  typeof WarehouseLayoutNodesReplaceSchema
>;

export const WarehouseLayoutGenerateSchema = z.object({
  branchId: z.string().uuid(),
  layoutId: z.string().uuid().optional(),
  overwrite: z.boolean().optional().default(true),
});
export type WarehouseLayoutGenerateDTO = z.infer<
  typeof WarehouseLayoutGenerateSchema
>;

export const WarehouseLayoutBackgroundSchema = z.object({
  branchId: z.string().uuid(),
  layoutId: z.string().uuid().optional(),
  backgroundImageUrl: z.string().min(1).max(2000).nullable(),
  backgroundOpacity: z.number().int().min(0).max(100).optional(),
});
export type WarehouseLayoutBackgroundDTO = z.infer<
  typeof WarehouseLayoutBackgroundSchema
>;

export const WarehouseLayoutDeleteQuerySchema = z.object({
  branchId: z.string().uuid(),
  layoutId: z.string().uuid().optional(),
});
export type WarehouseLayoutDeleteQueryDTO = z.infer<
  typeof WarehouseLayoutDeleteQuerySchema
>;

/** ອ້າງອີງ serial ຕໍ່ຊິ້ນ — serialId ຫຼື serialNo ຢ່າງໃດຢ່າງໜຶ່ງ */
export const PosCheckoutSerialRefSchema = z
  .object({
    serialId: z.string().uuid().optional(),
    serialNo: z.string().max(100).optional(),
  })
  .superRefine((s, ctx) => {
    const hasId = Boolean(s.serialId);
    const noTrim =
      s.serialNo === undefined || s.serialNo === "" ? "" : s.serialNo.trim();
    const hasNo = noTrim.length > 0;
    if (hasId === hasNo) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "serial ຕ້ອງມີ serialId ຫຼື serialNo ຢ່າງໃດຢ່າງໜຶ່ງ",
      });
    }
  });

/** ຊຳລະ POS — ລາຄາຄິດໃໝ່ຈາກ DB; ບັນທຶກການຊຳລະ + ຕັດສະຕ໋ອກ (ຖ້າມີແຖວ inventory) */
export const PosCheckoutLineSchema = z
  .object({
    shopProductSkuId: z.string().uuid(),
    qty: z.number().int().min(1).max(99_999),
    lotId: z.string().uuid().optional(),
    lotNo: z.string().max(100).optional(),
    serials: z.array(PosCheckoutSerialRefSchema).max(200).optional(),
    /** ສ່ວນຫຼຸດຕໍ່ລາຍການ (ຈຳນວນເງິນ) — ກວດສູງສຸດຝັ່ງ server ກັບຍອດລາຍການ */
    lineDiscountAmount: z.number().nonnegative().max(1e12).optional(),
    /** ສ່ວນຫຼຸດຕໍ່ລາຍການ (%) */
    lineDiscountPercent: z.number().nonnegative().max(100).optional(),
  })
  .superRefine((line, ctx) => {
    const lotNoTrim =
      line.lotNo === undefined || line.lotNo === ""
        ? undefined
        : line.lotNo.trim();
    if (line.lotId && lotNoTrim) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ສົ່ງໄດ້ແຕ່ lotId ຫຼື lotNo ຢ່າງໃດຢ່າງໜຶ່ງ",
      });
    }
    const serialCount = line.serials?.length ?? 0;
    if (serialCount > 0) {
      if (serialCount !== line.qty) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ຈຳນວນ serial ຕ້ອງເທົ່າກັບ qty",
        });
      }
      if (line.lotId || lotNoTrim) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "ບໍ່ສາມາດສົ່ງ lot ກັບ serial ຮ່ວມກັນ",
        });
      }
    }
    if (
      line.lineDiscountAmount != null &&
      line.lineDiscountAmount > 0 &&
      line.lineDiscountPercent != null &&
      line.lineDiscountPercent > 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ເລືອກໄດ້ແຕ່ lineDiscountAmount ຫຼື lineDiscountPercent",
      });
    }
  });
export type PosCheckoutLineDTO = z.infer<typeof PosCheckoutLineSchema>;
export type PosCheckoutSerialRefDTO = z.infer<
  typeof PosCheckoutSerialRefSchema
>;

/** Query ລາຍການ lot ສຳລັບ POS (ເລືອກ lot ຕອນຂາຍ) */
export const PosLotsQuerySchema = z.object({
  branchId: z.string().uuid(),
  shopProductSkuId: z.string().uuid(),
});
export type PosLotsQueryDTO = z.infer<typeof PosLotsQuerySchema>;

/** Query ລາຍການ serial in_stock ສຳລັບ POS */
export const PosSerialsQuerySchema = PosLotsQuerySchema;
export type PosSerialsQueryDTO = z.infer<typeof PosSerialsQuerySchema>;

/** ຄົ້ນ serial ຕາມລະຫັດ (ສະແກນ) */
export const PosSerialLookupQuerySchema = z.object({
  branchId: z.string().uuid(),
  code: z.string().min(1).max(100),
});
export type PosSerialLookupQueryDTO = z.infer<
  typeof PosSerialLookupQuerySchema
>;

/** ປະກອບລຳດັບສະແກນ: unit_scan → serial_no → sku barcode */
export const PosScanResolveQuerySchema = PosSerialLookupQuerySchema;
export type PosScanResolveQueryDTO = z.infer<typeof PosScanResolveQuerySchema>;

/** ບັນທັດຊຳລະຕໍ່ບິນ — ຮອງຮັບຫຼາຍຊ່ອງທາງ / ແບ່ງຈ່າຍ (ລວມເທົ່າກັບຍອດບິນ) */
export const PosCheckoutPaymentLineSchema = z.object({
  method: z.enum(["cash", "transfer", "wallet"]),
  amount: z.number().positive().max(1e12),
  /** ບໍ່ສົ່ງ = ສະກຸນບິນ */
  currency: ShopCurrencySchema.optional(),
  reference: z
    .string()
    .max(255)
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
});
export type PosCheckoutPaymentLineDTO = z.infer<
  typeof PosCheckoutPaymentLineSchema
>;

const emptyInvoiceFieldToUndef = (v: unknown) => {
  if (v === undefined || v === null) return undefined;
  if (typeof v === "string" && v.trim() === "") return undefined;
  return v;
};

/** ຂໍ້ມູນຜູ້ຊື້ສຳລັບໃບກຳກັບ — ບັນທຶກ snapshot ຕອນຊຳລະ */
export const PosInvoiceBuyerSnapshotSchema = z.object({
  buyerName: z.preprocess(
    emptyInvoiceFieldToUndef,
    z.string().trim().max(255).optional(),
  ),
  buyerTaxId: z.preprocess(
    emptyInvoiceFieldToUndef,
    z.string().trim().max(64).optional(),
  ),
  buyerAddress: z.preprocess(
    emptyInvoiceFieldToUndef,
    z.string().trim().max(2000).optional(),
  ),
});
export type PosInvoiceBuyerSnapshotDTO = z.infer<
  typeof PosInvoiceBuyerSnapshotSchema
>;

export const PosCheckoutSchema = z
  .object({
    lines: z.array(PosCheckoutLineSchema).min(1).max(200),
    payments: z.array(PosCheckoutPaymentLineSchema).min(1).max(20),
    /** ສ່ວນຫຼຸດທັງບິນ (ຫຼັງຫັກຕໍ່ລາຍການແລ້ວ) */
    billDiscountAmount: z.number().nonnegative().max(1e12).optional(),
    /** ສ່ວນຫຼຸດທັງບິນ (%) */
    billDiscountPercent: z.number().nonnegative().max(100).optional(),
    /** MC4 — ສະກຸນບິນ; ບັງຄັບເມື່ອມີຫຼາຍສະກຸນໃນລາຍການ */
    invoiceCurrency: ShopCurrencySchema.optional(),
    /** optional — ໃບກຳກັບພາສີ (ຊື່ / ເລກຜູ້ເສຍພາສີ / ທີ່ຢູ່ຜູ້ຊື້) */
    invoice: PosInvoiceBuyerSnapshotSchema.optional(),
    /** VAT  (%) — ບໍ່ສົ່ງ = ໃຊ້ຄ່າ default ຂອງຮ້ານ */
    taxPercent: z.number().min(0).max(100).optional(),
    /**
     * true = ຊຳລະມັດຈຳ (ຍອດຊຳລະຕ້ອງຕ່ຳກວ່າຍອດເຕັມຕາມກົດ slack) — ບິນເປັນ partial
     */
    partialPayment: z.boolean().optional(),
  })
  .superRefine((body, ctx) => {
    if (
      body.billDiscountAmount != null &&
      body.billDiscountAmount > 0 &&
      body.billDiscountPercent != null &&
      body.billDiscountPercent > 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ເລືອກໄດ້ແຕ່ billDiscountAmount ຫຼື billDiscountPercent",
        path: ["billDiscountPercent"],
      });
    }
  });
export type PosCheckoutDTO = z.infer<typeof PosCheckoutSchema>;

/** ພັກບິນ — ບໍ່ມີການຊຳລະ; ຢືນຢັນທີ່ POST /holds/:id/confirm */
export const PosCreateHoldSchema = z
  .object({
    lines: z.array(PosCheckoutLineSchema).min(1).max(200),
    billDiscountAmount: z.number().nonnegative().max(1e12).optional(),
    billDiscountPercent: z.number().nonnegative().max(100).optional(),
    /** MC4 — ສະກຸນບິນ; ບັງຄັບເມື່ອມີຫຼາຍສະກຸນໃນລາຍການ */
    invoiceCurrency: ShopCurrencySchema.optional(),
    holdTtlMinutes: z.number().int().min(5).max(10_080).optional().default(120),
  })
  .superRefine((body, ctx) => {
    if (
      body.billDiscountAmount != null &&
      body.billDiscountAmount > 0 &&
      body.billDiscountPercent != null &&
      body.billDiscountPercent > 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ເລືອກໄດ້ແຕ່ billDiscountAmount ຫຼື billDiscountPercent",
        path: ["billDiscountPercent"],
      });
    }
  });
export type PosCreateHoldDTO = z.infer<typeof PosCreateHoldSchema>;

/** ຕົວຢ່າງຍອດກ່ອນຊຳລະ — ຄິດສ່ວນຫຼຸດໂປຣ + ມື + ທ້າຍບິນ ແບບດຽວກັບ checkout (ບໍ່ຂຽນ DB) */
export const PosCheckoutPreviewSchema = z
  .object({
    lines: z.array(PosCheckoutLineSchema).min(1).max(200),
    billDiscountAmount: z.number().nonnegative().max(1e12).optional(),
    billDiscountPercent: z.number().nonnegative().max(100).optional(),
    invoiceCurrency: ShopCurrencySchema.optional(),
    /** VAT (%) — ບໍ່ສົ່ງ = ຄ່າ default ຮ້ານ */
    taxPercent: z.number().min(0).max(100).optional(),
  })
  .superRefine((body, ctx) => {
    if (
      body.billDiscountAmount != null &&
      body.billDiscountAmount > 0 &&
      body.billDiscountPercent != null &&
      body.billDiscountPercent > 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "ເລືອກໄດ້ແຕ່ billDiscountAmount ຫຼື billDiscountPercent",
        path: ["billDiscountPercent"],
      });
    }
  });
export type PosCheckoutPreviewDTO = z.infer<typeof PosCheckoutPreviewSchema>;

export const PosHoldConfirmSchema = z.object({
  payments: z.array(PosCheckoutPaymentLineSchema).min(1).max(20),
  invoice: PosInvoiceBuyerSnapshotSchema.optional(),
  taxPercent: z.number().min(0).max(100).optional(),
});
export type PosHoldConfirmDTO = z.infer<typeof PosHoldConfirmSchema>;

/** ເພີ່ມຊຳລະຕໍ່ບິນ partial */
export const PosSaleAddPaymentsSchema = z.object({
  payments: z.array(PosCheckoutPaymentLineSchema).min(1).max(20),
});
export type PosSaleAddPaymentsDTO = z.infer<typeof PosSaleAddPaymentsSchema>;

export const PosSaleIdParamSchema = z.object({
  saleId: z.string().uuid(),
});
export type PosSaleIdParamDTO = z.infer<typeof PosSaleIdParamSchema>;

// ===== POS Return / Exchange =====
export const PosReturnIdParamSchema = z.object({
  returnId: z.string().uuid(),
});
export type PosReturnIdParamDTO = z.infer<typeof PosReturnIdParamSchema>;

export const CreatePosReturnLineSchema = z.object({
  saleItemId: z.string().uuid(),
  qty: z.coerce.number().int().min(1),
  restock: z.boolean().default(true),
});

export const CreatePosReturnSchema = z.object({
  originalSaleId: z.string().uuid(),
  type: z.enum(["return", "exchange"]).default("return"),
  reason: z.string().max(2000).optional(),
  refundMethod: z
    .enum(["cash", "transfer", "wallet", "store_credit"])
    .optional(),
  /** exchange: บิลขายใหม่ที่สร้างผ่าน checkout ก่อนแล้ว (ผูกกัน) */
  exchangeSaleId: z.string().uuid().optional(),
  lines: z.array(CreatePosReturnLineSchema).min(1),
});
export type CreatePosReturnDTO = z.infer<typeof CreatePosReturnSchema>;

export const PosReturnListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
});
export type PosReturnListQueryDTO = z.infer<typeof PosReturnListQuerySchema>;

const emptyQueryStringToUndef = (v: unknown) => {
  if (v === undefined || v === null) return undefined;
  if (typeof v === "string" && v.trim() === "") return undefined;
  return v;
};

/** ສຳລັບ GET ລາຍການ POS sale — YYYY-MM-DD ຫຼື ISO string */
export function parsePosSalesListDateBoundary(
  s: string,
  endOfDay: boolean,
): Date | null {
  const t = s.trim();
  if (!t) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  if (m) {
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    const dt = endOfDay
      ? new Date(Date.UTC(y, mo - 1, d, 23, 59, 59, 999))
      : new Date(Date.UTC(y, mo - 1, d, 0, 0, 0, 0));
    return Number.isNaN(dt.getTime()) ? null : dt;
  }
  const dt = new Date(t);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

export const PosSalesListQuerySchema = z
  .object({
    branchId: z.string().uuid().optional(),
    status: z
      .enum(["draft", "paid", "voided", "refunded", "partial"])
      .optional(),
    /** ກອງບິນທີ່ມີການ ຄືນ/ປ່ຽນ ສຳເລັດ (pos_returns.type) — ແຍກຈາກ status */
    returnType: z.enum(["return", "exchange"]).optional(),
    /** ວັນທີເລີ່ມ (YYYY-MM-DD ຫຼື ISO datetime) — ກອງ created_at >= */
    createdFrom: z.preprocess(
      emptyQueryStringToUndef,
      z.string().trim().max(80).optional(),
    ),
    /** ວັນທີສິ້ນສຸດ (ລວມ) — YYYY-MM-DD ຈະຖືກເປັນທ້າຍວັນ UTC */
    createdTo: z.preprocess(
      emptyQueryStringToUndef,
      z.string().trim().max(80).optional(),
    ),
    /** ຄົ້ນເລກບິນ (ບາງສ່ວນ, case-insensitive) */
    saleNumber: z.preprocess(
      emptyQueryStringToUndef,
      z.string().trim().min(1).max(64).optional(),
    ),
    /** ລູກຄ້າ platform user id (ຕົງກັນ) */
    customerUserId: z.preprocess(
      emptyQueryStringToUndef,
      z.string().trim().min(1).max(128).optional(),
    ),
    /** ຄົ້ນລູກຄ້າຈາກຊື່ / ອີເມວ (ມີລູກຄ້າຕິດບິນເທົ່ານັ້ນ) */
    customerSearch: z.preprocess(
      emptyQueryStringToUndef,
      z.string().trim().min(1).max(200).optional(),
    ),
    limit: z.coerce.number().int().min(1).max(100).optional().default(20),
    offset: z.coerce.number().int().min(0).optional().default(0),
  })
  .superRefine((val, ctx) => {
    if (val.createdFrom != null) {
      if (parsePosSalesListDateBoundary(val.createdFrom, false) == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Invalid createdFrom",
          path: ["createdFrom"],
        });
      }
    }
    if (val.createdTo != null) {
      if (parsePosSalesListDateBoundary(val.createdTo, true) == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Invalid createdTo",
          path: ["createdTo"],
        });
      }
    }
  });

/** ກອງຊ່ວງວັນທີ ສຳລັບລາຍງານສະຫຼຸບຕາມສະກຸນ (§3 ທາງເລືອກ 1) */
export const PosSalesByCurrencyReportQuerySchema = z
  .object({
    branchId: z.string().uuid().optional(),
    /** ຄ່າຄັ່ນ comma ຫຼືຊ້ຳ query — ກອງຫຼາຍສາຂາ */
    branchIds: z.preprocess((raw) => {
      if (raw === undefined || raw === null || raw === "") return undefined;
      if (Array.isArray(raw)) return raw;
      if (typeof raw === "string") {
        const parts = raw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        return parts.length > 0 ? parts : undefined;
      }
      return undefined;
    }, z.array(z.string().uuid()).max(30).optional()),
    createdFrom: z.preprocess(
      emptyQueryStringToUndef,
      z.string().trim().max(80).optional(),
    ),
    createdTo: z.preprocess(
      emptyQueryStringToUndef,
      z.string().trim().max(80).optional(),
    ),
  })
  .superRefine((val, ctx) => {
    if (
      val.branchId != null &&
      val.branchIds != null &&
      val.branchIds.length > 0
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Use either branchId or branchIds, not both",
        path: ["branchId"],
      });
    }
    if (val.createdFrom != null) {
      if (parsePosSalesListDateBoundary(val.createdFrom, false) == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Invalid createdFrom",
          path: ["createdFrom"],
        });
      }
    }
    if (val.createdTo != null) {
      if (parsePosSalesListDateBoundary(val.createdTo, true) == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Invalid createdTo",
          path: ["createdTo"],
        });
      }
    }
  });
export type PosSalesByCurrencyReportQueryDTO = z.infer<
  typeof PosSalesByCurrencyReportQuerySchema
>;

/** Top SKU + ລາຍງານຕໍ່ວັນ — ກົດຊ່ວງ/ສາຂາ ດຽວກັບລາຍງານສະກຸນ; ຈຳກວດ top */
export const PosSalesExtendedReportQuerySchema =
  PosSalesByCurrencyReportQuerySchema.extend({
    limit: z.coerce.number().int().min(1).max(50).optional().default(20),
  });
export type PosSalesExtendedReportQueryDTO = z.infer<
  typeof PosSalesExtendedReportQuerySchema
>;

export type PosSalesListQueryDTO = z.infer<typeof PosSalesListQuerySchema>;

// ---------------------------------------------------------------------------
// Purchase orders (P0)
// ---------------------------------------------------------------------------

export const PurchaseOrderLineInputSchema = z.object({
  shopProductSkuId: z.string().uuid(),
  qtyOrdered: z.number().int().min(1),
  unitCost: z.union([z.number().finite().nonnegative(), z.null()]).optional(),
  lineNote: z
    .string()
    .max(2000)
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
});
export type PurchaseOrderLineInputDTO = z.infer<
  typeof PurchaseOrderLineInputSchema
>;

export const CreatePurchaseOrderSchema = z.object({
  branchId: z.string().uuid(),
  currency: ShopCurrencySchema.optional(),
  supplierName: z
    .string()
    .max(255)
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
  supplierRef: z
    .string()
    .max(128)
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
  orderedAt: z
    .string()
    .max(80)
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
  expectedAt: z
    .string()
    .max(80)
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
  note: z
    .string()
    .max(5000)
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
  /** P3: ເລືອກຈາກຕາຕະລາງ suppliers — ລະບົບຈະເຕີມຊື່/ref ຈາກຜູ້ສະໜອງ */
  supplierId: z.string().uuid().optional(),
  lines: z.array(PurchaseOrderLineInputSchema).default([]),
});
export type CreatePurchaseOrderDTO = z.infer<typeof CreatePurchaseOrderSchema>;

export const UpdatePurchaseOrderSchema = z
  .object({
    branchId: z.string().uuid().optional(),
    currency: ShopCurrencySchema.optional(),
    supplierName: z
      .string()
      .max(255)
      .nullable()
      .optional()
      .transform((v) => {
        if (v === undefined) return undefined;
        if (v === null) return null;
        const t = v.trim();
        return t === "" ? null : t;
      }),
    supplierRef: z
      .string()
      .max(128)
      .nullable()
      .optional()
      .transform((v) => {
        if (v === undefined) return undefined;
        if (v === null) return null;
        const t = v.trim();
        return t === "" ? null : t;
      }),
    supplierId: z.string().uuid().nullable().optional(),
    orderedAt: z
      .string()
      .max(80)
      .nullable()
      .optional()
      .transform((v) => {
        if (v === undefined) return undefined;
        if (v === null) return null;
        const t = v.trim();
        return t === "" ? null : t;
      }),
    expectedAt: z
      .string()
      .max(80)
      .nullable()
      .optional()
      .transform((v) => {
        if (v === undefined) return undefined;
        if (v === null) return null;
        const t = v.trim();
        return t === "" ? null : t;
      }),
    note: z
      .string()
      .max(5000)
      .nullable()
      .optional()
      .transform((v) => {
        if (v === undefined) return undefined;
        if (v === null) return null;
        const t = v.trim();
        return t === "" ? null : t;
      }),
    lines: z.array(PurchaseOrderLineInputSchema).optional(),
  })
  .refine(
    (d) =>
      d.branchId !== undefined ||
      d.currency !== undefined ||
      d.supplierName !== undefined ||
      d.supplierRef !== undefined ||
      d.supplierId !== undefined ||
      d.orderedAt !== undefined ||
      d.expectedAt !== undefined ||
      d.note !== undefined ||
      d.lines !== undefined,
    { message: "ຕ້ອງມີຢ່າງນ້ອຍໜຶ່ງຟິລດ໌ທີ່ຈະອັບເດດ" },
  );
export type UpdatePurchaseOrderDTO = z.infer<typeof UpdatePurchaseOrderSchema>;

export const PurchaseOrderListSortFieldSchema = z.enum([
  "createdAt",
  "poNumber",
  "status",
  "currency",
  "branchName",
  "lineCount",
]);
export type PurchaseOrderListSortField = z.infer<
  typeof PurchaseOrderListSortFieldSchema
>;

export const PurchaseOrderListQuerySchema = z.object({
  status: z
    .enum(["draft", "submitted", "partially_received", "received", "canceled"])
    .optional(),
  branchId: z.string().uuid().optional(),
  /** P3: ກອງຕາມຜູ້ສະໜອງຈາກຕາຕະລາງ */
  supplierId: z.string().uuid().optional(),
  /** P3: ກຳນົດສົ່ງ — ໃກ້ຄວຄິດ (7 ວັນ) ຫຼືເກີນກຳນົດ */
  due: z.enum(["soon", "overdue"]).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  offset: z.coerce.number().int().min(0).optional().default(0),
  sortBy: PurchaseOrderListSortFieldSchema.optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
});
export type PurchaseOrderListQueryDTO = z.infer<
  typeof PurchaseOrderListQuerySchema
>;

// ---------------------------------------------------------------------------
// Suppliers (P3)
// ---------------------------------------------------------------------------

export const SupplierListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional().default(50),
  offset: z.coerce.number().int().min(0).optional().default(0),
  search: z.string().max(200).optional(),
  /** ກັບ PO combobox ຍັງສົ່ງແຕ່ activeOnly=true — ບໍ່ສົ່ງ activeFilter */
  activeOnly: z.coerce.boolean().optional(),
  /** ລາຍການຜູ້ສະໜອງໃນແອັບ: ກອງຕາມສະຖານະ (ມີລຳດັບຄວາມສຳຄັນສູງກວ່າ activeOnly) */
  activeFilter: z.enum(["all", "active", "inactive"]).optional(),
});
export type SupplierListQueryDTO = z.infer<typeof SupplierListQuerySchema>;

export const CreateSupplierSchema = z.object({
  name: z.string().min(1).max(255),
  phone: z.string().max(64).nullable().optional(),
  refCode: z.string().max(128).nullable().optional(),
  note: z.string().max(2000).nullable().optional(),
});
export type CreateSupplierDTO = z.infer<typeof CreateSupplierSchema>;

export const UpdateSupplierSchema = z
  .object({
    name: z.string().min(1).max(255).optional(),
    phone: z.string().max(64).nullable().optional(),
    refCode: z.string().max(128).nullable().optional(),
    note: z.string().max(2000).nullable().optional(),
    isActive: z.boolean().optional(),
  })
  .refine(
    (d) =>
      d.name !== undefined ||
      d.phone !== undefined ||
      d.refCode !== undefined ||
      d.note !== undefined ||
      d.isActive !== undefined,
    { message: "ຕ້ອງມີຢ່າງນ້ອຍໜຶ່ງຟິລດ໌" },
  );
export type UpdateSupplierDTO = z.infer<typeof UpdateSupplierSchema>;

export const SupplierIdParamSchema = z.object({
  supplierId: z.string().uuid(),
});
export type SupplierIdParamDTO = z.infer<typeof SupplierIdParamSchema>;

// ---------------------------------------------------------------------------
// Shop promotions (P8) — กฎต่อร้าน MVP: line_percent_off / line_amount_off
// ---------------------------------------------------------------------------

export const ShopPromotionStatusSchema = z.enum(["draft", "active", "paused"]);
export type ShopPromotionStatusDTO = z.infer<typeof ShopPromotionStatusSchema>;

export const ShopPromotionRuleTypeSchema = z.enum([
  "line_percent_off",
  "line_amount_off",
]);
export type ShopPromotionRuleTypeDTO = z.infer<
  typeof ShopPromotionRuleTypeSchema
>;

export const ShopPromotionListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional().default(50),
  offset: z.coerce.number().int().min(0).optional().default(0),
  status: z
    .enum(["all", "draft", "active", "paused"])
    .optional()
    .default("all"),
  /** ຄົ້ນຫາຊື່ໂປຣ (ilike) */
  search: z.string().max(200).optional(),
});
export type ShopPromotionListQueryDTO = z.infer<
  typeof ShopPromotionListQuerySchema
>;

const ShopCurrencyOptSchema = z
  .string()
  .max(10)
  .optional()
  .nullable()
  .transform((v) => (v == null || v.trim() === "" ? null : v.trim()));

const BasePromotionBodySchema = z.object({
  name: z.string().min(1).max(200).trim(),
  status: ShopPromotionStatusSchema.optional().default("draft"),
  priority: z.coerce.number().int().min(0).max(1_000_000).optional().default(0),
  /** null/ວ່າງ = ບໍ່ຈຳກັດ – ຕົ້ນ ຫຼື ປາຍຊ່ວງ (ISO ຈາກ API) */
  startsAt: z.preprocess(
    (v) => (v === "" || v === undefined ? null : v),
    z.union([z.string().min(1), z.null()]),
  ),
  endsAt: z.preprocess(
    (v) => (v === "" || v === undefined ? null : v),
    z.union([z.string().min(1), z.null()]),
  ),
  branchId: z.string().uuid().optional().nullable(),
  billCurrency: ShopCurrencyOptSchema,
  ruleType: ShopPromotionRuleTypeSchema,
  percentOff: z.coerce.number().min(0).max(100).optional().nullable(),
  amountOff: z.coerce.number().min(0).max(1e12).optional().nullable(),
  targetSkuIds: z.array(z.string().uuid()).default([]),
});

export const CreateShopPromotionSchema = BasePromotionBodySchema.superRefine(
  (data, ctx) => {
    const s = data.startsAt != null ? new Date(data.startsAt) : null;
    const e = data.endsAt != null ? new Date(data.endsAt) : null;
    if (s != null && Number.isNaN(+s)) {
      ctx.addIssue({
        code: "custom",
        path: ["startsAt"],
        message: "Invalid date",
      });
    }
    if (e != null && Number.isNaN(+e)) {
      ctx.addIssue({
        code: "custom",
        path: ["endsAt"],
        message: "Invalid date",
      });
    }
    if (s != null && e != null && e <= s) {
      ctx.addIssue({
        code: "custom",
        path: ["endsAt"],
        message: "endsAt must be after startsAt",
      });
    }
    if (data.ruleType === "line_percent_off") {
      if (
        data.percentOff == null ||
        !Number.isFinite(data.percentOff) ||
        data.percentOff <= 0
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["percentOff"],
          message: "percentOff required",
        });
      }
    }
    if (data.ruleType === "line_amount_off") {
      if (
        data.amountOff == null ||
        !Number.isFinite(data.amountOff) ||
        data.amountOff <= 0
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["amountOff"],
          message: "amountOff required",
        });
      }
    }
  },
);
export type CreateShopPromotionDTO = z.infer<typeof CreateShopPromotionSchema>;

export const UpdateShopPromotionSchema = z
  .object({
    name: z.string().min(1).max(200).trim().optional(),
    status: ShopPromotionStatusSchema.optional(),
    priority: z.coerce.number().int().min(0).max(1_000_000).optional(),
    startsAt: z.preprocess(
      (v) => (v === "" ? null : v),
      z.union([z.string().min(1), z.null()]).optional(),
    ),
    endsAt: z.preprocess(
      (v) => (v === "" ? null : v),
      z.union([z.string().min(1), z.null()]).optional(),
    ),
    branchId: z.string().uuid().optional().nullable(),
    billCurrency: ShopCurrencyOptSchema,
    ruleType: ShopPromotionRuleTypeSchema.optional(),
    percentOff: z.coerce.number().min(0).max(100).optional().nullable(),
    amountOff: z.coerce.number().min(0).max(1e12).optional().nullable(),
    targetSkuIds: z.array(z.string().uuid()).optional(),
  })
  .refine((d) => Object.keys(d).length > 0, { message: "At least one field" })
  .superRefine((data, ctx) => {
    if (data.startsAt != null || data.endsAt != null) {
      const s = data.startsAt != null ? new Date(data.startsAt) : null;
      const e = data.endsAt != null ? new Date(data.endsAt) : null;
      if (s && e && !Number.isNaN(+s) && !Number.isNaN(+e) && e <= s) {
        ctx.addIssue({
          code: "custom",
          path: ["endsAt"],
          message: "ends after starts",
        });
      }
    }
    const rt = data.ruleType;
    if (
      rt === "line_percent_off" &&
      data.percentOff != null &&
      data.percentOff <= 0
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["percentOff"],
        message: "must be > 0",
      });
    }
    if (
      rt === "line_amount_off" &&
      data.amountOff != null &&
      data.amountOff <= 0
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["amountOff"],
        message: "must be > 0",
      });
    }
  });
export type UpdateShopPromotionDTO = z.infer<typeof UpdateShopPromotionSchema>;

export const ShopPromotionIdParamSchema = z.object({
  promotionId: z.string().uuid(),
});
export type ShopPromotionIdParamDTO = z.infer<
  typeof ShopPromotionIdParamSchema
>;

export const PurchaseOrderIdParamSchema = z.object({
  purchaseOrderId: z.string().uuid(),
});
export type PurchaseOrderIdParamDTO = z.infer<
  typeof PurchaseOrderIdParamSchema
>;

// ---------------------------------------------------------------------------
// Product issue claims (Claim C0)
// ---------------------------------------------------------------------------

export const ProductIssueTypeSchema = z.enum([
  "defect",
  "wrong_part",
  "short_ship",
  "expired",
  "other",
]);
export type ProductIssueTypeDTO = z.infer<typeof ProductIssueTypeSchema>;

export const ProductClaimStatusSchema = z.enum([
  "draft",
  "submitted",
  "under_review",
  "resolved",
  "rejected",
  "canceled",
]);

export const ShopProductClaimLineInputSchema = z.object({
  shopProductSkuId: z.string().uuid(),
  qty: z.coerce.number().int().positive(),
  posSaleItemId: z.string().uuid().optional().nullable(),
  inventoryLotId: z.string().uuid().optional().nullable(),
  inventorySerialId: z.string().uuid().optional().nullable(),
  lineNote: z
    .string()
    .max(2000)
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
});

export const WarrantyEligibilityAtSubmitSchema = z.enum([
  "in_warranty",
  "out_of_warranty",
  "unknown",
  "not_applicable",
]);
export type WarrantyEligibilityAtSubmitDTO = z.infer<
  typeof WarrantyEligibilityAtSubmitSchema
>;

export const CreateShopProductClaimSchema = z.object({
  branchId: z.string().uuid(),
  /** Claim C2 — ຜູ້ສະໜອງ (ລາຍງານ) */
  supplierId: z.string().uuid().optional().nullable(),
  /** W2 — ອ້າງສິດປະກັນຕາມບຣັດທາງ (ຕ້ອງກົງກັບ pos_sale + ບັນທັດເຄສ) */
  saleLineWarrantyId: z.string().uuid().optional().nullable(),
  issueType: ProductIssueTypeSchema,
  title: z
    .string()
    .max(255)
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
  description: z
    .string()
    .max(8000)
    .optional()
    .transform((v) => {
      if (v === undefined || v === "") return undefined;
      const t = v.trim();
      return t === "" ? undefined : t;
    }),
  posSaleId: z.string().uuid().optional().nullable(),
  /** W3 — ເລກໄມລ໌ຕອນເປີດເຄສ (ໃຊ້ເມື່ອປະກັນມີເລກໄມລ໌ສູງສຸດ) */
  odometerAtClaimKm: z
    .union([z.number().int().min(0).max(9_999_999), z.null()])
    .optional(),
  /** W3 — ລູກຄ້າ/ຍານພາຫະນະ */
  vehicleNote: z
    .string()
    .max(500)
    .optional()
    .nullable()
    .transform((v) => {
      if (v === undefined || v === null) return v;
      const t = v.trim();
      return t === "" ? null : t;
    }),
  lines: z.array(ShopProductClaimLineInputSchema).min(1),
});
export type CreateShopProductClaimDTO = z.infer<
  typeof CreateShopProductClaimSchema
>;

export const UpdateShopProductClaimDraftSchema = z.object({
  issueType: ProductIssueTypeSchema.optional(),
  supplierId: z.string().uuid().optional().nullable(),
  title: CreateShopProductClaimSchema.shape.title,
  description: CreateShopProductClaimSchema.shape.description,
  posSaleId: z.string().uuid().optional().nullable(),
  saleLineWarrantyId: z.string().uuid().optional().nullable(),
  odometerAtClaimKm: z
    .union([z.number().int().min(0).max(9_999_999), z.null()])
    .optional(),
  vehicleNote: CreateShopProductClaimSchema.shape.vehicleNote,
  lines: z.array(ShopProductClaimLineInputSchema).min(1).optional(),
});
export type UpdateShopProductClaimDraftDTO = z.infer<
  typeof UpdateShopProductClaimDraftSchema
>;

export const ShopProductClaimWarrantyLinkFilterSchema = z.enum([
  "any",
  "linked",
  "none",
]);

export const ShopProductClaimListQuerySchema = z.object({
  branchId: z.string().uuid(),
  status: ProductClaimStatusSchema.optional(),
  /** Claim C2 — ກອງຕາມຜູ້ສະໜອງ */
  supplierId: z.string().uuid().optional(),
  /** W2 — ກອງຕາມການຜູກປະກັນ */
  warrantyLink:
    ShopProductClaimWarrantyLinkFilterSchema.optional().default("any"),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ShopProductClaimListQueryDTO = z.infer<
  typeof ShopProductClaimListQuerySchema
>;

/** W3 — ລາຍງານປະກັນໃກ້ຫມົດອາຍຸ */
export const ShopWarrantiesExpiringQuerySchema = z.object({
  branchId: z.string().uuid(),
  withinDays: z.coerce.number().int().min(1).max(365).default(30),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
});
export type ShopWarrantiesExpiringQueryDTO = z.infer<
  typeof ShopWarrantiesExpiringQuerySchema
>;

export const ShopProductClaimIdParamSchema = z.object({
  claimId: z.string().uuid(),
});
export type ShopProductClaimIdParamDTO = z.infer<
  typeof ShopProductClaimIdParamSchema
>;

export const SetShopProductClaimStatusSchema = z
  .object({
    status: z.enum(["under_review", "resolved", "rejected"]),
    /** Claim C2 — ຕັດສະຕ໋ອກຊຳຮຸດຕາມລາຍບັນທັດ (ສະເພາະ resolved) */
    applyStockImpact: z.boolean().optional().default(false),
    resolutionNote: z
      .string()
      .max(8000)
      .optional()
      .transform((v) => {
        if (v === undefined || v === "") return undefined;
        const t = v.trim();
        return t === "" ? undefined : t;
      }),
  })
  .refine(
    (d) =>
      d.status === "under_review" ||
      (d.resolutionNote !== undefined && d.resolutionNote.length > 0),
    {
      message: "ຕ້ອງມີຄຳອະທິບາຍເມື່ອປິດເຄສ (resolved/rejected)",
      path: ["resolutionNote"],
    },
  )
  .refine((d) => !d.applyStockImpact || d.status === "resolved", {
    message: "applyStockImpact ສະເພາະຕອນປິດເຄສ (resolved)",
    path: ["applyStockImpact"],
  });
export type SetShopProductClaimStatusDTO = z.infer<
  typeof SetShopProductClaimStatusSchema
>;

export const AddShopProductClaimAttachmentSchema = z.object({
  storageKey: z.string().min(1).max(2000),
  contentType: z
    .string()
    .max(255)
    .optional()
    .nullable()
    .transform((v) => (v === "" ? null : v)),
  fileName: z
    .string()
    .max(255)
    .optional()
    .nullable()
    .transform((v) => (v === "" ? null : v)),
});
export type AddShopProductClaimAttachmentDTO = z.infer<
  typeof AddShopProductClaimAttachmentSchema
>;

export const ShopProductClaimAttachmentIdParamSchema = z.object({
  claimId: z.string().uuid(),
  attachmentId: z.string().uuid(),
});
export type ShopProductClaimAttachmentIdParamDTO = z.infer<
  typeof ShopProductClaimAttachmentIdParamSchema
>;

// ---------------------------------------------------------------------------
// P7 — Settlement / wallet payout (GET/POST ໃຕ້ /shops/:shopId/settlement)
// ---------------------------------------------------------------------------

export const PosSettlementStatusQuerySchema = z.enum([
  "pending",
  "processing",
  "paid_out",
  "failed",
]);
export type PosSettlementStatusQueryDTO = z.infer<
  typeof PosSettlementStatusQuerySchema
>;

export const ShopSettlementPeriodsQuerySchema = OffsetPageQuerySchema.extend({
  status: PosSettlementStatusQuerySchema.optional(),
  createdFrom: z.string().datetime().optional(),
  createdTo: z.string().datetime().optional(),
});
export type ShopSettlementPeriodsQueryDTO = z.infer<
  typeof ShopSettlementPeriodsQuerySchema
>;

export const PayoutRequestStatusQuerySchema = z.enum([
  "requested",
  "approved",
  "processing",
  "paid",
  "failed",
  "rejected",
]);
export type PayoutRequestStatusQueryDTO = z.infer<
  typeof PayoutRequestStatusQuerySchema
>;

export const ShopPayoutRequestsQuerySchema = OffsetPageQuerySchema.extend({
  status: PayoutRequestStatusQuerySchema.optional(),
  createdFrom: z.string().datetime().optional(),
  createdTo: z.string().datetime().optional(),
  currency: ShopCurrencySchema.optional(),
});
export type ShopPayoutRequestsQueryDTO = z.infer<
  typeof ShopPayoutRequestsQuerySchema
>;

export const ShopSettlementPeriodIdParamSchema = z.object({
  id: z.string().uuid(),
});
export type ShopSettlementPeriodIdParamDTO = z.infer<
  typeof ShopSettlementPeriodIdParamSchema
>;

export const CreateShopPayoutRequestSchema = z.object({
  amount: z.coerce.number().positive().max(999_999_999.99),
  currency: ShopCurrencySchema,
  payoutMethod: z.enum(["bank_transfer", "cash"]),
  /** ບັນຊີ / ຊື່ — ສຳລັບ bank_transfer; ອາຈເວັ້ນເປົ່າເມື່ອ cash */
  payoutDestination: z.unknown().optional(),
});
export type CreateShopPayoutRequestDTO = z.infer<
  typeof CreateShopPayoutRequestSchema
>;

export const PublishMarketplaceListingSchema = z.object({
  shopProductSkuId: z.string().uuid(),
  fulfillmentBranchId: z.string().uuid().optional().nullable(),
});
export type PublishMarketplaceListingDTO = z.infer<
  typeof PublishMarketplaceListingSchema
>;

export const MarketplaceSettingsPatchSchema = z.object({
  autoPublish: z.boolean(),
});
export type MarketplaceSettingsPatchDTO = z.infer<
  typeof MarketplaceSettingsPatchSchema
>;

export const MarketplaceListingIdParamSchema = z.object({
  listingId: z.string().uuid(),
});

export const ShopMarketplaceListQuerySchema = OffsetPageQuerySchema.extend({
  q: z.string().trim().optional(),
});
export type ShopMarketplaceListQueryDTO = z.infer<
  typeof ShopMarketplaceListQuerySchema
>;
